import { useEffect, useRef, useState } from "react";
import type { PdfDocument } from "../lib/pdf";
import {
  blocksInRegion,
  splitTranslationRegions,
  translationFontSize
} from "../lib/translationLayout";
import type {
  LayoutRegion,
  PageLayout,
  TextBlock,
  TranslationBlock
} from "../types";

interface PdfPageProps {
  document: PdfDocument;
  page: PageLayout;
  zoom: number;
  side: "source" | "translation";
  translations: Record<string, TranslationBlock>;
  highlightedId: string | null;
  onHighlight: (id: string | null) => void;
  onEdit: (block: TextBlock) => void;
}

type SharedPageProps = Omit<PdfPageProps, "side">;

function SourcePage({
  document,
  page,
  zoom,
  highlightedId,
  onHighlight
}: SharedPageProps) {
  const wrapper = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [visible, setVisible] = useState(page.pageNumber <= 2);
  const [rendered, setRendered] = useState(false);
  const [renderError, setRenderError] = useState("");

  useEffect(() => {
    const node = wrapper.current;
    if (!node || !("IntersectionObserver" in window)) {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) setVisible(true);
      },
      { rootMargin: "900px 0px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible || !canvas.current) return;
    let cancelled = false;
    let renderTask: { cancel: () => void; promise: Promise<unknown> } | null =
      null;

    void document
      .getPage(page.pageNumber)
      .then((pdfPage) => {
        if (cancelled || !canvas.current) return;
        const deviceScale = Math.min(window.devicePixelRatio || 1, 2);
        const viewport = pdfPage.getViewport({ scale: zoom * deviceScale });
        const cssViewport = pdfPage.getViewport({ scale: zoom });
        const context = canvas.current.getContext("2d", { alpha: false });
        if (!context) throw new Error("浏览器无法创建 PDF 画布。");
        canvas.current.width = Math.ceil(viewport.width);
        canvas.current.height = Math.ceil(viewport.height);
        canvas.current.style.width = `${cssViewport.width}px`;
        canvas.current.style.height = `${cssViewport.height}px`;
        renderTask = pdfPage.render({
          canvas: canvas.current,
          canvasContext: context,
          viewport
        });
        return renderTask.promise;
      })
      .then(() => {
        if (!cancelled) {
          setRendered(true);
          setRenderError("");
        }
      })
      .catch((reason) => {
        if (!cancelled && reason?.name !== "RenderingCancelledException") {
          setRenderError("本页渲染失败");
        }
      });

    return () => {
      cancelled = true;
      renderTask?.cancel();
    };
  }, [document, page.pageNumber, visible, zoom]);

  return (
    <div
      ref={wrapper}
      className={`pdf-page source-pdf-page ${rendered ? "is-rendered" : ""}`}
      data-page={page.pageNumber}
      style={{ width: page.width * zoom, height: page.height * zoom }}
    >
      <canvas ref={canvas} aria-label={`第 ${page.pageNumber} 页`} />
      {!rendered && !renderError && <span className="page-skeleton" />}
      {renderError && <span className="page-render-error">{renderError}</span>}
      <div className="block-layer source">
        {page.blocks
          .filter((block) => block.translatable)
          .map((block) => (
            <button
              key={block.id}
              data-block-id={block.id}
              className={`source-block ${
                highlightedId === block.id ? "is-highlighted" : ""
              }`}
              style={{
                left: block.bbox.x * zoom,
                top: block.bbox.y * zoom,
                width: Math.max(block.bbox.width * zoom, 3),
                height: Math.max(block.bbox.height * zoom, 3)
              }}
              onMouseEnter={() => onHighlight(block.id)}
              onMouseLeave={() => onHighlight(null)}
              aria-label={`英文段落：${block.text.slice(0, 80)}`}
            />
          ))}
      </div>
      <span className="page-number-label">{page.pageNumber}</span>
    </div>
  );
}

function TranslationRegion({
  region,
  page,
  zoom,
  translations,
  highlightedId,
  onHighlight,
  onEdit
}: {
  region: LayoutRegion;
  page: PageLayout;
  zoom: number;
  translations: Record<string, TranslationBlock>;
  highlightedId: string | null;
  onHighlight: (id: string | null) => void;
  onEdit: (block: TextBlock) => void;
}) {
  return (
    <section
      className="translation-flow-region"
      data-region-id={region.id}
      aria-label={
        region.kind === "column"
          ? `第 ${region.columnIndex + 1} 栏译文`
          : "全宽译文区域"
      }
    >
      {blocksInRegion(page, region.id).map((block) => {
        const translation = translations[block.id];
        const isReady =
          translation?.status === "done" || translation?.status === "edited";
        const isTranslating = translation?.status === "translating";
        const text = isReady ? translation.text : block.text;
        return (
          <button
            key={block.id}
            data-block-id={block.id}
            className={`translation-flow-block role-${block.role} ${
              highlightedId === block.id ? "is-highlighted" : ""
            } ${isReady ? "is-ready" : "is-placeholder"}`}
            style={{ fontSize: translationFontSize(block, zoom) }}
            onMouseEnter={() => onHighlight(block.id)}
            onMouseLeave={() => onHighlight(null)}
            onClick={() => {
              if (isReady) onEdit(block);
            }}
            aria-label={
              isReady ? "点击校对这段译文" : "等待翻译的英文段落"
            }
          >
            {isTranslating && <span className="translation-flow-shimmer" />}
            <span>{text}</span>
          </button>
        );
      })}
    </section>
  );
}

function TranslationPage(props: SharedPageProps) {
  const {
    page,
    zoom,
    translations,
    highlightedId,
    onHighlight,
    onEdit
  } = props;
  const sections = splitTranslationRegions(page);
  const renderRegion = (region: LayoutRegion) => (
    <TranslationRegion
      key={region.id}
      region={region}
      page={page}
      zoom={zoom}
      translations={translations}
      highlightedId={highlightedId}
      onHighlight={onHighlight}
      onEdit={onEdit}
    />
  );

  return (
    <article
      className="pdf-page translation-flow-page"
      data-page={page.pageNumber}
      style={{
        width: page.width * zoom,
        minHeight: page.height * zoom
      }}
    >
      <div className="translation-flow-content">
        {sections.top.map(renderRegion)}
        {sections.columns.length > 0 && (
          <div
            className="translation-flow-columns"
            style={{
              gridTemplateColumns: `repeat(${sections.columns.length}, minmax(0, 1fr))`
            }}
          >
            {sections.columns.map(renderRegion)}
          </div>
        )}
        {sections.bottom.map(renderRegion)}
        {!page.regions.length && (
          <p className="translation-empty-page">本页没有可翻译正文。</p>
        )}
      </div>
      <span className="page-number-label">{page.pageNumber}</span>
    </article>
  );
}

export function PdfPage(props: PdfPageProps) {
  return props.side === "source" ? (
    <SourcePage {...props} />
  ) : (
    <TranslationPage {...props} />
  );
}
